import { PROGRAMS_BUDGET_MAP, PROGRAMS_MEQ } from 'components/common/vendorConfig';
import moment from 'moment';
import type { FoodOrder } from 'types';
import { mapKey } from 'utils';

/**
 * The slice of a caller's props these calculators read.
 *
 * Structural rather than any one component's props type: callers across
 * `food/index.tsx`, `draftOrders/index.tsx`, `orders/index.tsx`,
 * `orders/add.tsx` and `orders/update.tsx` hand their whole `props` object in,
 * and `foodUtils.spec.ts` passes bare literals.
 *
 * Only `allReferralData[0]` is ever consulted — the first referral stands in for
 * the member's current one.
 */
type MealCalcProps = {
  org?: string | null | undefined;
  /**
   * Explicit `| undefined` rather than `ReferralSummary` (types/patient-data.ts):
   * `foodUtils.spec.ts` writes `referralId`/`orgId` unconditionally from optional
   * parameters, which under `exactOptionalPropertyTypes` is a pass of
   * `undefined` and `ReferralSummary`'s plain optionals reject. The shared type
   * is right about the feed, where these keys are absent rather than undefined.
   */
  allReferralData?:
    | {
        referralId?: string | undefined;
        orgId?: string | undefined;
        referralDate?: string | undefined;
      }[]
    | undefined;
};

/*
 * SUSPECTED DEFECT (line 42 and line 118) — REPORTED, NOT FIXED.
 * Both eligibility checks contain `(referralOrgId ?? referralOrgId === props.org)`.
 * `??` binds tighter than the comparison here, so this reads "if `referralOrgId`
 * is non-nullish, use it (truthy for any non-empty string); otherwise evaluate
 * `undefined === props.org`". The intended test is plainly
 * `referralOrgId === props.org`. As written, ANY referral carrying an `orgId`
 * satisfies the arm regardless of which org it belongs to, so a member with a
 * referral from a different payer is treated as program-eligible and their meal
 * equivalents and spend are counted against this org's budget.
 *
 * The `referralId.indexOf(props.org) > -1` arm on the left usually masks it,
 * because it fails first for a foreign referral — but the two are OR'd, so the
 * broken arm decides whenever the id prefix does not match. Left as written: the
 * fix changes which orders count toward a budget, which is a product decision.
 *
 * SUSPECTED DEFECT (throughout both functions) — REPORTED, NOT FIXED.
 * Every status test is `order.status.toUpperCase() !== '…'`, unguarded, while
 * {@link FoodOrder.status} is optional — the orders feed does omit it, which is
 * why the tables all render it through a `? :`. An order without a status throws
 * a TypeError here, inside the loop that totals a member's meal equivalents and
 * spend, so one such row takes down the whole budget calculation rather than
 * being skipped. The `!` assertions below record that assumption; adding `?.`
 * would silently exclude those orders from the totals instead, which is a
 * different wrong answer.
 *
 * The `parseInt(order.mealEquivalents)` and `parseFloat(itemPrice)` calls below
 * are left as standing TS2345s. `mealEquivalents` is a `number` on
 * {@link FoodOrder} and the price fields are `number | string`, while both
 * parsers declare a `string` parameter — at runtime the numeric arm is coerced
 * via `toString`, which is why this works. Asserting `as string` would claim
 * these are strings when they are not; the diagnostic is the accurate report.
 */

/**
 * The slice of an order these calculators read.
 *
 * `Omit<FoodOrder, 'orderItems'>` plus a length-only `orderItems`: both
 * functions consult `orderItems` solely for `.length`, never for an element's
 * fields (the loop that read `quantity`/`noOfServings` is commented out). That
 * also keeps `foodUtils.spec.ts`'s `[{}]` fixtures legal.
 */
type CalculableOrder = Omit<FoodOrder, 'orderItems'> & {
  orderItems?: { length: number } | undefined;
};

export function calculateMealEquivalents(
  order: CalculableOrder,
  allowCancelled: boolean,
  props: MealCalcProps
) {
  if (isOtherGoodsOrder(order)) {
    return 0;
  }
  let tempMealEquivalents = 0;
  let isOrderDateLatest = false;
  let isProgramMeqEligible = false;
  const allReferralData = props?.allReferralData?.[0];
  const referralId = allReferralData?.referralId;
  const referralOrgId = allReferralData?.orgId;

  if (
    referralId &&
    PROGRAMS_MEQ.includes(props.org!) &&
    (referralId.indexOf(props.org!) > -1 || (referralOrgId ?? referralOrgId === props.org))
  ) {
    isProgramMeqEligible = true;

    const referralDate = allReferralData?.referralDate;
    const tempReferralDate = moment.utc(referralDate).local().format('YYYY-MM-DD hh:mm a');
    const tempOrderDate = moment.utc(order.orderDate).local().format('YYYY-MM-DD hh:mm a');
    isOrderDateLatest = moment(tempOrderDate).isSameOrAfter(tempReferralDate);
  }

  // CATALOG ORDERS (orderItems)
  if (
    order.orderItems &&
    order?.orderItems.length > 0 &&
    (allowCancelled ||
      (order.status!.toUpperCase() !== 'REJECTED' &&
        order.status!.toUpperCase() !== 'CANCELLED' &&
        order.status!.toUpperCase() !== 'DRAFT' &&
        order.status!.toUpperCase() !== 'UNFULFILLED' &&
        order.status!.toUpperCase() !== 'UNACCEPTED'))
  ) {
    // Check with Business
    // order.orderItems.forEach((item) => {
    //   let itemQty =
    //     (item?.quantity ? parseInt(item.quantity) : 1) * (item?.noOfServings ? parseInt(item.noOfServings) : 1);
    //   tempMealEquivalents += itemQty;
    // });

    if (!isProgramMeqEligible && isOrderDateLatest) {
      tempMealEquivalents += order?.mealEquivalents ? parseInt(String(order.mealEquivalents)) : 0;
    } else if (isProgramMeqEligible && isOrderDateLatest) {
      tempMealEquivalents += order?.mealEquivalents ? parseInt(String(order.mealEquivalents)) : 0;
    }
  } else if (
    // STANDARD ORDERS (orderItems)
    order?.orderType &&
    (order?.orderType.toUpperCase() === 'STANDARD ORDER' || order?.orderType.toUpperCase() === 'GAP ORDER') &&
    (allowCancelled ||
      (order.status!.toUpperCase() !== 'REJECTED' &&
        order.status!.toUpperCase() !== 'CANCELLED' &&
        order.status!.toUpperCase() !== 'DRAFT'))
  ) {
    // TO DO (check whether the bundle quantities for the old orders to be considered)
    // let orderQty = order?.mealEquivalents
    //   ? parseInt(order.mealEquivalents)
    //   : order?.bundleQuantity
    //   ? parseInt(order.bundleQuantity)
    //   : 1;
    const orderQty = order?.mealEquivalents ? parseInt(String(order.mealEquivalents)) : 0;
    if (!isProgramMeqEligible) {
      tempMealEquivalents += orderQty;
    } else if (isProgramMeqEligible && isOrderDateLatest) {
      tempMealEquivalents += orderQty;
    }

    // console.log(`order Id: ${order.orderName} = ${orderQty} = Total = ${quantity}`);
    // } else if (
    //   order?.orderType &&
    //   order?.orderType.toUpperCase() !== 'CATALOG' &&
    //   order.status.toUpperCase() !== 'CANCELLED' &&
    //   order.status.toUpperCase() !== 'EBT PENDING'
    // ) {
    //   // console.log('&&&&&&&&', order?.bundleQuantity);
    //   let orderQty = order?.bundleQuantity ? parseInt(order.bundleQuantity) : 1;
    //   quantity += orderQty;
    //   // console.log(`order Id: ${order.orderName} = ${orderQty} = Total = ${quantity}`);
  } else {
    // Do Nothing. Mostly EBT and GAP orders come here
  }
  return tempMealEquivalents;
}

export function calculateMealPricing(
  order: CalculableOrder,
  allowCancelled: boolean,
  props: MealCalcProps
) {
  if (isOtherGoodsOrder(order)) {
    return 0;
  }
  let tempOrdersCost = 0;
  let isOrderDateLatest = false;
  let isProgramEligible = false;
  const allReferralData = props?.allReferralData?.[0];
  const referralId = allReferralData?.referralId;
  const referralOrgId = allReferralData?.orgId;
  let tempPrice = 0;

  if (
    referralId &&
    PROGRAMS_BUDGET_MAP?.[mapKey(props?.org?.toUpperCase())]?.ACTIVE &&
    (referralId.indexOf(props.org!) > -1 || (referralOrgId ?? referralOrgId === props.org))
  ) {
    isProgramEligible = true;

    const referralDate = allReferralData?.referralDate;
    const tempReferralDate = moment.utc(referralDate).local().format('YYYY-MM-DD hh:mm a');
    const tempOrderDate = moment.utc(order.orderDate).local().format('YYYY-MM-DD hh:mm a');
    isOrderDateLatest = moment(tempOrderDate).isSameOrAfter(moment(tempReferralDate));
    console.log(
      'Referral Date:',
      tempReferralDate,
      'Order Date:',
      tempOrderDate,
      'isOrderDateLatest:',
      isOrderDateLatest
    );
    const shippingFees = order?.shippingFees || 0;
    const itemPrice = order?.foodPricing || 0;
    // Both operands are `number | string` off `FoodOrder`, defaulted to the
    // number `0` on the lines above, while `parseFloat` declares a `string` —
    // the same pre-existing mismatch documented in `invoices/index.tsx`. Left
    // standing rather than asserted, since half these values genuinely are
    // numbers.
    tempPrice =
      parseFloat(String(itemPrice)) +
      (PROGRAMS_BUDGET_MAP?.[mapKey(props?.org?.toUpperCase())]?.SHIPPING_INCLUDED ? parseFloat(String(shippingFees)) : 0);
    tempPrice = Math.round(tempPrice * 100) / 100; // Round to 2 decimal places
  }

  // CATALOG ORDERS (orderItems)
  if (
    order.orderItems &&
    order?.orderItems.length > 0 &&
    (allowCancelled ||
      (order.status!.toUpperCase() !== 'REJECTED' &&
        order.status!.toUpperCase() !== 'CANCELLED' &&
        order.status!.toUpperCase() !== 'DRAFT' &&
        order.status!.toUpperCase() !== 'UNFULFILLED' &&
        order.status!.toUpperCase() !== 'UNACCEPTED'))
  ) {
    // console.log('temp: ', isOrderDateLatest);
    if (!isProgramEligible && isOrderDateLatest) {
      // tempOrdersCost += order?.foodPricing ? parseInt(order.foodPricing) : 0;
      // tempOrdersCost += tempPrice;
    } else if (isProgramEligible && isOrderDateLatest) {
      tempOrdersCost += tempPrice;
      // tempOrdersCost += order?.foodPricing ? parseInt(order.foodPricing) : 0;
    }
  } else if (
    // STANDARD ORDERS (orderItems)
    order?.orderType &&
    (order?.orderType.toUpperCase() === 'STANDARD ORDER' || order?.orderType.toUpperCase() === 'GAP ORDER') &&
    (allowCancelled ||
      (order.status!.toUpperCase() !== 'REJECTED' &&
        order.status!.toUpperCase() !== 'CANCELLED' &&
        order.status!.toUpperCase() !== 'DRAFT' &&
        order.status!.toUpperCase() !== 'UNACCEPTED'))
  ) {
    tempOrdersCost += tempPrice;
  } else {
    // Do Nothing. Mostly EBT and GAP orders come here
  }
  return tempOrdersCost;
}

/**
 * Returns true when an order belongs to the food-card program (e.g. FRC / Fresh Connect).
 */
export function isFoodCardOrder(order: FoodOrder | null | undefined): boolean {
  if (!order) {
    return false;
  }

  const foodTypeName = (order.foodTypeName ?? '').toLowerCase();
  const bundleTypeName = (order.bundleTypeName ?? '').toLowerCase();

  return (
    foodTypeName === 'food card' ||
    bundleTypeName === 'food card' ||
    order.foodTypeCode === 'FC' ||
    order.bundleTypeCode === 'FC'
  );
}

/**
 * Returns true when an order is Other Goods (Diapers & Wipes / `DW`), not food delivery.
 *
 * These orders must not count toward program food budget or meal equivalents.
 *
 * Takes `CalculableOrder` rather than `FoodOrder` because the two calculators call it
 * with their own parameter, and `CalculableOrder`'s length-only `orderItems` is not
 * assignable to `FoodOrderItem[]`.
 */
export function isOtherGoodsOrder(order: CalculableOrder | null | undefined): boolean {
  if (!order) {
    return false;
  }

  const foodTypeName = (order.foodTypeName ?? '').toLowerCase();
  const bundleTypeName = (order.bundleTypeName ?? '').toLowerCase();
  const typeCodes = [order.foodTypeCode, order.bundleTypeCode]
    .filter(Boolean)
    .flatMap((code) =>
      String(code)
        .split('|')
        .map((token) => token.trim().toUpperCase())
    );

  return (
    typeCodes.includes('DW') ||
    foodTypeName === 'diapers & wipes' ||
    bundleTypeName === 'diapers & wipes'
  );
}
